import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["classes", "onChange", "slotProps", "disableCloseOnSelect", "value", "defaultValue", "OptionEndIconComponent", "OptionStartIconComponent", "showOptionIcon", "helperText", "label", "onBlur", "onFocus", "placeholder", "TextFieldProps", "required", "error", "onOpen", "onClose", "open", "edsContext"];
import React, { useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { default as MuiAutocomplete, createFilterOptions } from '@mui/material/Autocomplete';
import uuid from 'uuid/v4';
import isNil from 'lodash/isNil';
import { default as withStyles } from '../styles';
import TextField from '../TextField/TextField';
import InputAdornment from '../InputAdornment/InputAdornment';
import Chip from '../Chip/Chip';
import renderOptionsWithIcons from './internal/renderOptionsWithIcons';
import { withEDSContext } from './../EDSContext/EDSContext';
import ChevronDown from '@ellucian/ds-icons/lib/ChevronDown';
import CloseIcon from '@ellucian/ds-icons/lib/Close';
import { borderRadiusCircle, borderRadiusSmall, borderWidthThin, fontSizeDefault, fontSizeLarge, paddingTextField, spacing20, spacing30, spacing40, spacing60, spacing70, widthInputFields } from '../styles/tokens';
var styles = function styles(theme) {
  return {
    root: {
      width: widthInputFields,
      '&.MuiAutocomplete-fullWidth': {
        width: '100%'
      }
    },
    textFieldRoot: {
      '& .MuiFormControl-root': {
        width: widthInputFields,
        '&.MuiFormControl-fullWidth': {
          width: '100%'
        }
      },
      '& .MuiOutlinedInput-root': {
        padding: paddingTextField,
        height: 'auto',
        '& .MuiAutocomplete-input': {
          padding: 0
        },
        '& .MuiSvgIcon-root': {
          color: theme.palette.grey[500],
          height: spacing40,
          '&:hover': {
            color: theme.palette.ctaColor.hover
          }
        },
        '& .MuiIconButton-root': {
          '&:hover svg': {
            fill: theme.palette.ctaColor.hover
          },
          '& svg': {
            fill: theme.palette.grey[500],
            fontSize: fontSizeLarge
          }
        },
        '& .MuiAutocomplete-tag': {
          margin: ".125rem " + spacing20 + " 0 0"
        },
        '& .MuiChip-deleteIcon': {
          margin: "0 0.3125rem 0 -" + spacing30,
          borderRadius: borderRadiusCircle,
          padding: spacing20,
          '&:hover': {
            backgroundColor: theme.palette.grey[300]
          },
          '&:active': {
            backgroundColor: theme.palette.grey[400]
          }
        }
      },
      '& .MuiAutocomplete-endAdornment': {
        top: "calc(50% - " + spacing40 + ")"
      },
      '& .MuiAutocomplete-popupIndicator': {
        borderRadius: borderRadiusSmall,
        height: spacing60,
        width: spacing60,
        '&:hover': {
          backgroundColor: 'transparent'
        }
      },
      '& .MuiAutocomplete-clearIndicator': {
        borderRadius: borderRadiusSmall,
        height: spacing60,
        width: spacing60,
        visibility: 'visible',
        '&:hover': {
          backgroundColor: 'transparent'
        }
      }
    },
    popper: {
      '& .MuiAutocomplete-listbox': {
        padding: 0
      },
      '& .MuiAutocomplete-option': {
        height: 'auto',
        minHeight: spacing70,
        borderBottom: borderWidthThin + " solid " + theme.palette.divider,
        '&:last-child': {
          borderBottom: 'none'
        },
        '&.Mui-focused, &.Mui-focusVisible': {
          backgroundColor: theme.palette.grey[250],
          '&:hover, &:focus': {
            color: theme.palette.grey[600]
          },
          '&:active': {
            backgroundColor: theme.palette.ctaColor.active,
            color: theme.palette.grey['100'],
            '& svg': {
              fill: theme.palette.grey['100']
            }
          }
        },
        '&[aria-selected="true"]': {
          backgroundColor: theme.palette.grey['300'],
          '&.Mui-focused': {
            backgroundColor: theme.palette.grey['250'],
            color: theme.palette.grey['600'],
            '&:hover, &:focus': {
              '&:active': {
                backgroundColor: theme.palette.ctaColor.active,
                color: theme.palette.grey['100'],
                '& svg': {
                  fill: theme.palette.grey['100'],
                  color: theme.palette.grey['100']
                }
              }
            }
          },
          '&:active': {
            backgroundColor: theme.palette.ctaColor.active,
            color: theme.palette.grey['100'],
            '& svg': {
              fill: theme.palette.grey['100'],
              color: theme.palette.grey['100']
            }
          }
        }
      }
    },
    fullWidth: {},
    small: {
      '& .MuiAutocomplete-endAdornment': {
        top: "calc(50% - " + spacing40 + ")",
        '& .MuiIconButton-root': {
          '& svg': {
            fontSize: fontSizeDefault
          }
        }
      }
    },
    hidePopupIcon: {
      '& .MuiAutocomplete-popupIndicator': {
        display: 'none'
      }
    }
  };
};

/**
 * A dropdown that (optionally) supports the selection of multiple items, and provides a search to narrow down the list of available options.
 * @done true
 * @updated v7.11.0
 * @versionAdded v7.7.0
 * @examples
 *  AutocompleteExample
 *  AutocompleteDisabled
 *  AutocompleteControlledStates
 *  AutocompleteRequired
 *  AutocompleteMultipleSelectionExample
 *  AutocompleteWithIcons
 *  AutocompleteWithDifferentIcons
 *  AutocompleteMultipleSelectionIcons
 *  AutocompleteFreeSolo
 *  AutocompleteAsyncOnOpen
 *  AutocompleteVirtualization
 *  AutocompleteWithFilterOptions
 */
var Autocomplete = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var classes = props.classes,
    onChangeProp = props.onChange,
    slotProps = props.slotProps,
    disableCloseOnSelectProp = props.disableCloseOnSelect,
    valueProp = props.value,
    defaultValueProp = props.defaultValue,
    OptionEndIconComponent = props.OptionEndIconComponent,
    OptionStartIconComponent = props.OptionStartIconComponent,
    showOptionIcon = props.showOptionIcon,
    helperText = props.helperText,
    label = props.label,
    onBlurProp = props.onBlur,
    onFocus = props.onFocus,
    placeholder = props.placeholder,
    TextFieldProps = props.TextFieldProps,
    required = props.required,
    error = props.error,
    onOpenProp = props.onOpen,
    onCloseProp = props.onClose,
    openProp = props.open,
    formatMessage = props.edsContext.formatMessage,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  var _useState = useState(''),
    startAdornment = _useState[0],
    setStartAdornment = _useState[1];
  var renderOptionProp = useMemo(function () {
    return function (props, option, state, ownerState) {
      return renderOptionsWithIcons({
        props: props,
        option: option,
        state: state,
        ownerState: ownerState,
        OptionStartIconComponent: OptionStartIconComponent,
        OptionEndIconComponent: OptionEndIconComponent
      });
    };
  }, [OptionStartIconComponent, OptionEndIconComponent]);

  // Logic to prevent MUI Autocomplete null value/defaultValue crash
  var value = valueProp,
    defaultValue = defaultValueProp;
  if (props.multiple && props.value !== undefined && !Array.isArray(props.value)) {
    value = [];
  }
  if (props.multiple && props.defaultValue !== undefined && !Array.isArray(props.defaultValue)) {
    defaultValue = [];
  }
  var disableCloseOnSelect = disableCloseOnSelectProp || false;
  if (props.multiple) {
    disableCloseOnSelect = true;
  }

  // if there is a value, hide the popup icon
  var _useState2 = useState(defaultValue || value || ''),
    internalValue = _useState2[0],
    setInternalValue = _useState2[1];
  var hidePopupIcon = !isNil(internalValue) && internalValue.length !== 0; // not null/undefined and has a length

  var clearText = formatMessage('component.Autocomplete.clear');
  var loadingText = formatMessage('component.Autocomplete.loading');
  var noOptionsText = formatMessage('component.Autocomplete.noResults');
  var openText = formatMessage('component.Autocomplete.open');
  var onChange = function onChange(event, newValue, reason) {
    setInternalValue((newValue == null ? void 0 : newValue.label) || newValue);
    if (onChangeProp) {
      onChangeProp(event, newValue, reason);
    }
    if (showOptionIcon) {
      newValue ? setStartAdornment((newValue == null ? void 0 : newValue.startIconComponent) || OptionStartIconComponent) : setStartAdornment(null);
    }
  };
  var uniqueId = useMemo(function () {
    return uuid();
  }, []);
  var _useState3 = useState(false),
    open = _useState3[0],
    setOpen = _useState3[1];

  // this is toggled to false when the component detects a blur that occurs when a
  // VoiceOver user attempts to select a dropdown item
  var allowDropdownClose = useRef(true);

  // if the component is controlled, invoke the provided callback; otherwise we handle
  // open state internally
  var onOpen = function onOpen() {
    if (onOpenProp) {
      onOpenProp.apply(void 0, arguments);
    } else {
      setOpen(true);
    }
  };

  // if the component is controlled, invoke the provided callback; otherwise we handle
  // open state internally
  var onClose = function onClose() {
    if (allowDropdownClose) {
      if (onCloseProp) {
        onCloseProp.apply(void 0, arguments);
      } else {
        setOpen(false);
      }
    }
    allowDropdownClose = true;
  };
  var onBlur = function onBlur(e) {
    var _e$relatedTarget, _e$relatedTarget2;
    // iPhone VoiceOver blurs the input field when the user attempts to select an
    // item from the dropdown list, or navigates to the list toggle button; this
    // closes the list before a selection can occur

    // the hack below attempts to address this issue by detecting that blur (ie, a blur
    // whose target is the dropdown or the button), and suppressing the dropdown close.

    if ((_e$relatedTarget = e.relatedTarget) != null && _e$relatedTarget.closest != null && _e$relatedTarget.closest("[data-popper-id=\"" + uniqueId + "\"]") || (_e$relatedTarget2 = e.relatedTarget) != null && _e$relatedTarget2.closest != null && _e$relatedTarget2.closest("[data-textfield-id=\"" + uniqueId + "\"]")) {
      allowDropdownClose = false;
    } else {
      allowDropdownClose = true;
    }
    if (onBlurProp) {
      onBlurProp(e);
    }
  };
  return /*#__PURE__*/React.createElement(MuiAutocomplete, _extends({
    ref: ref,
    classes: {
      root: classes.root,
      popper: classes.popper
    },
    popupIcon: /*#__PURE__*/React.createElement(ChevronDown, null),
    clearIcon: /*#__PURE__*/React.createElement(CloseIcon, null),
    value: value,
    defaultValue: defaultValue,
    disableCloseOnSelect: disableCloseOnSelect,
    onChange: onChange,
    renderInput: function renderInput(params) {
      var _classNames;
      var InputPropsProp = !props.multiple && showOptionIcon ? _extends({}, params.InputProps, {
        startAdornment: startAdornment && /*#__PURE__*/React.createElement(InputAdornment, {
          position: "start",
          style: {
            margin: '0 .375rem 0 0'
          }
        }, startAdornment)
      }) : params.InputProps;
      return /*#__PURE__*/React.createElement(TextField, _extends({}, params, {
        className: classNames(classes.textFieldRoot, (_classNames = {}, _classNames[classes.fullWidth] = props.fullWidth, _classNames[classes.small] = props.size === 'small', _classNames[classes.hidePopupIcon] = hidePopupIcon, _classNames)),
        label: label,
        required: required,
        error: error,
        onBlur: onBlur,
        onFocus: onFocus,
        helperText: helperText,
        placeholder: placeholder,
        InputProps: InputPropsProp,
        "data-textfield-id": uniqueId
      }, TextFieldProps));
    },
    renderTags: function renderTags(values, getTagProps) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, values.map(function (option, index) {
        return /*#__PURE__*/React.createElement(Chip, _extends({
          key: index,
          icon: props.multiple && showOptionIcon ? option.startIconComponent || OptionStartIconComponent : undefined,
          label: typeof option === 'string' ? option : option.label
        }, getTagProps({
          index: index
        }), {
          sx: {
            '& svg:first-of-type': {
              ml: '.875rem'
            }
          }
        }));
      }));
    },
    renderOption: renderOptionProp,
    clearText: clearText,
    loadingText: loadingText,
    noOptionsText: noOptionsText,
    openText: openText,
    slotProps: _extends({
      popper: _extends({
        modifiers: [{
          name: 'offset',
          options: {
            offset: [0, 4]
          }
        }]
      }, slotProps == null ? void 0 : slotProps.popper),
      paper: _extends({
        elevation: 4,
        'data-popper-id': uniqueId
      }, slotProps == null ? void 0 : slotProps.paper)
    }, slotProps),
    open: isNil(openProp) ? open : openProp,
    onOpen: onOpen,
    onClose: onClose
  }, rest));
});
Autocomplete.muiName = 'Autocomplete';
Autocomplete.displayName = 'Autocomplete';
Autocomplete.propTypes = {
  /**
   * Autocomplete specific props
   */
  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  /**
   * Array of options.
   *
   * When each option is an object, it must have a `label` property assigned.
   */
  options: PropTypes.array.isRequired,
  /**
   * An icon component that appears at the beginning of each option in the `Autocomplete` dropdown.
   *
   * You can override this icon by passing `startIconComponent` in the `options` model.
   */
  OptionStartIconComponent: PropTypes.node,
  /**
   * An icon component that appears at the end of each option in the `Autocomplete` dropdown.
   *
   * You can override this icon by passing `endIconComponent` in the `options` model.
   */
  OptionEndIconComponent: PropTypes.node,
  /**
   * If set to `true`, the dropdown will display the `OptionStartIconComponent` or
   * the model's `startIconComponent` corresponding to the selected option in the input field.
   */
  showOptionIcon: PropTypes.bool,
  /**
   * The default value. Use when the component is not controlled.
   *
   * This should have strict-equality to one of the `options` sent in.
   */
  defaultValue: PropTypes.any,
  /**
   * The value of the `Autocomplete`. Use when the component is controlled.
   *
   * The value will return an `object` of the option selected (when `multiple` is false).
   * When the multiple prop is true, it will return an `Array` of option objects selected.
   *
   * We recommend setting this to `null` for single selection, or an empty Array when `multiple` is true.
   *
   * This should have strict-equality to one of the `options` sent in.
   */
  value: PropTypes.any,
  /**
   * Callback fired when the value changes.
   *
   * **Signature:**
   *
   * ```
   * function(event: React.SyntheticEvent, value: {} | Array, reason: string, details?: string) => void
   * ```
   *
   * * `event`: The event source of the callback.
   * * `value`: The value that uniquely identifies the selected item.
   * * `reason`: One of "createOption", "selectOption", "removeOption", "blur" or "clear".
   */
  onChange: PropTypes.func,
  /**
   * The input value.
   */
  inputValue: PropTypes.string,
  /**
    * Callback fired when the input value changes.
    *
    * **Signature:**
    *
    * ```function(event: React.SyntheticEvent, value: string, reason: string) => void```
    *
    * * `event` The event source of the callback.
    * * `value` The new value of the text input.
    * * `reason` Can be: "input" (user input), "reset" (programmatic change), "clear".
    */
  onInputChange: PropTypes.func,
  /**
   * @ignore -- internal
   *
   * Custom render the option
   *
   * * `props` The props to apply on the li element
   * * `option` The option to render
   * * `state` The state of each option
   * * `ownerState The state of the Autocomplete component
   */
  renderOption: PropTypes.func,
  /**
   * @ignore -- internal
   *
   * Render the input
   *
   * * `params` The props to apply on the li element
   */
  renderInput: PropTypes.func,
  /**
   * If `true`, the `Popper` content will be under the DOM hierarchy of the parent component.
   *
   */
  disablePortal: PropTypes.bool,
  /**
   * @ignore
   * If `true`, the popup won't close when a value is selected.
   *
   * This will be set to `true`, if `multiple` is true.
   */
  disableCloseOnSelect: PropTypes.bool,
  /**
   * If `true`, `value` must be an array and the menu will support multiple selections.
   */
  multiple: PropTypes.bool,
  /**
   * If `true`, the `Autocomplete` is free solo, meaning that the user input is not bound to provided options.
   */
  freeSolo: PropTypes.bool,
  /**
   * @ignore
   * If `true`, the portion of the selected suggestion that has not been typed by the user, known as the completion string,
   * appears inline after the input cursor in the textbox. This occurs after a user focuses on an option presented in the `Popper`.
   *
   * The inline completion string is visually highlighted and has a selected state.
   */
  autoComplete: PropTypes.bool,
  /**
   * @ignore
   * If `true`, the first option is automatically highlighted.
   */
  autoHighlight: PropTypes.bool,
  /**
   * @ignore
   * Used to determine if the option represents the given value. Uses strict equality by default.
   * ⚠️ Both arguments need to be handled, an option can only match with one value.
   *
   * Signature:
   * `function(option: Value, value: Value) => boolean`
   * * `options` The option to test
   * * `value` The value to test against.
   */
  isOptionEqualToValue: PropTypes.func,
  /**
   * A function that determines the filtered options to be rendered on search.
   * Signature:
   * `function(options: Array, state: object) => Array`
   * * `options The options to render.`
   * * `state` The state of the component.
   */
  filterOptions: PropTypes.func,
  /**
   * @ignore -- may want to introduce this later
   * If `true`, hide the selected options from the list box.
  */
  filterSelectedOptions: PropTypes.bool,
  /**
   * Used to determine the disabled state for a given option.
   *
   * **Signature:**
   *
   * `function(option: Value) => boolean`
   * * `option` The option to test.
   */
  getOptionDisabled: PropTypes.func,
  /**
   * If `true`, the component is in a loading state. This shows the `loadingText` in place of suggestions
   * (only if there are no suggestions to show, e.g. `options` are empty).
   *
   * The default `loadingText` can be changed via the translation key `component.Autocomplete.loadingText`.
   */
  loading: PropTypes.bool,
  /**
   * Callback fired when the popup requests to be closed. Use in controlled mode (see open).
   *
   * **Signature:**
   *
   * `function(event: React.SyntheticEvent, reason: string) => void`
   *
   * * `event` The even source of the callback.
   * * `reason` Can be: `'toggleInput'`, `'escape'`, `'selectOption'`, `'removeOption'`, `'blur'`
  */
  onClose: PropTypes.func,
  /**
   * Callback fired when the popup requests to be opened. Use in controlled mode (see open).
   *
   * **Signature:**
   *
   * `function(event: React.SyntheticEvent, reason: string) => void`
   *
   * * `event` The even source of the callback.
  */
  onOpen: PropTypes.func,
  /**
   * If `true`, the component is shown.
  */
  open: PropTypes.bool,
  /**
   * @ignore
   * If `true`, the popup will open on input focus.
  */
  openOnFocus: PropTypes.bool,
  /**
  * Callback fired when the highlight option changes.
  *
  * **Signature:**
  *
  * `function(event: React.SyntheticEvent, reason: string) => void`
  *
  *  * `event` The event source of the callback.
  *  * `option` The highlighted option.
  *  * `reason` Can be: `"keyboard"`, `"auto"`, `"mouse"`, `"touch"`.
  */
  onHighlightChange: PropTypes.func,
  /**
   * The component used to render the listbox.
   *
   * **Use with caution. This may override default styles.
   */
  ListboxComponent: PropTypes.elementType,
  /**
   * Props applied to the `Listbox` element.
   *
   * **Use with caution. This may override default styles.
   */
  ListboxProps: PropTypes.object,
  /**
   * @ignore
   * The props used for each slot inside.
   */
  slotProps: PropTypes.shape({
    clearIndicator: PropTypes.object,
    paper: PropTypes.object,
    popper: PropTypes.object,
    popupIndicator: PropTypes.object
  }),
  /**
   * ======================================
   * TextField specific props
   * ======================================
   */
  /**
   * Id assigned to the `input` element and referenced by `label` element
   */
  id: PropTypes.string,
  /**
   * Label that is attached to the `Autocomplete`
   */
  label: PropTypes.string,
  /**
   * Event handler when the input gets the `blur` event. This is applied to the `TextField` component.
   */
  onBlur: PropTypes.func,
  /**
   * Event handler when the input gets the `focus` event. This is applied to the `TextField` component.
   */
  onFocus: PropTypes.func,
  /**
   * The size of the `TextField` rendered.
   */
  size: PropTypes.oneOfType([PropTypes.oneOf(['small', 'medium']), PropTypes.string]),
  /**
   * If `true`, the `TextField` and `Popper` will take up the full width of its container.
   */
  fullWidth: PropTypes.bool,
  /**
   * If true, the component becomes `readonly`. It is also supported for multiple tags where the tag cannot be deleted.
   */
  readOnly: PropTypes.bool,
  /**
   * If true, the component becomes `disabled`.
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, the label is displayed as required.
   */
  required: PropTypes.bool,
  /**
   * If `true`, the label will be displayed in an error state.
   */
  error: PropTypes.bool,
  /**
   * The placeholder text content of the `TextField`.
   */
  placeholder: PropTypes.string,
  /**
   * The helper text content of the `TextField`.
   */
  helperText: PropTypes.string,
  /**
   * @ignore -- hide from doc site, keep available if consumer NEEDS to do something to Textfield.
   * Props applied to the TextField element. This may cause issues from the `params` sent via Autocomplete logic.
   */
  TextFieldProps: PropTypes.object,
  /**
  * The system prop that allows defining system overrides as well as additional CSS styles. This is applied to the root `div` element of `Autocomplete`.
  */
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object]),
  /**
   * @ignore
   */
  edsContext: PropTypes.object
};
Autocomplete.defaultProps = {
  autoComplete: true,
  autoHighlight: true,
  disablePortal: false,
  error: false,
  freeSolo: false,
  fullWidth: false,
  openOnFocus: true,
  readOnly: false,
  multiple: false,
  required: false,
  showOptionIcon: false,
  size: 'medium'
};
export { createFilterOptions };
export default withEDSContext(withStyles(styles)(Autocomplete));